import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, signal } from '@angular/core';
import { SelectionStore } from '../../services/stores/selection.store';
import { PaginationStore } from '../../services/stores/pagination.store';
import { WORLD_CLOCK_DATA } from './world-clock.data';
import { WorldClockLocalTimeLog, WorldClockLog } from '../../models/world-clock-log.model';
import { WorldClockTableComponent } from './world-clock-table/world-clock-table.component';

@Component({
  selector: 'dr-ops-stream-ui-world-clock',
  imports: [WorldClockTableComponent],
  templateUrl: './world-clock.component.html',
  styleUrl: './world-clock.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [PaginationStore, SelectionStore],
})
export class WorldClockComponent {
  // static rows
  private readonly baseRowsSignal = signal<WorldClockLog[]>(WORLD_CLOCK_DATA);

  // current UTC time
  private readonly currentUtcSignal = signal<Date>(new Date());

  private readonly timerId: number;

  readonly rows = computed<WorldClockLocalTimeLog[]>(() => {
    const time = this.currentUtcSignal();
    return this.baseRowsSignal().map((row) => {
      // Create formatter to get hours, minutes and offset natively
      const formatter = new Intl.DateTimeFormat(undefined, {
        timeZone: row.ianaTimeZone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'shortOffset'
      });

      const parts = formatter.formatToParts(time);
      const hour = parts.find(p => p.type === 'hour')?.value || '00';
      const minute = parts.find(p => p.type === 'minute')?.value || '00';
      let offsetStr = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT';

      // format offset string from GMT-7 to -7 or ±0
      if (offsetStr === 'GMT') {
        offsetStr = '±0';
      } else {
        offsetStr = offsetStr.replace('GMT', '');
      }
      let shortTzName = this.formatTimeZone(row.ianaTimeZone, time, row.locale) ?? row.timeZone;


      const localTime = `${hour}:${minute}`;

      // Determine day/night: [06:00, 18:00) is "day"
      const hourNum = parseInt(hour, 10);
      const isDay = !isNaN(hourNum) && hourNum >= 6 && hourNum < 18;

      return {
        ...row,
        timeZone: shortTzName,
        utcOffset: offsetStr,
        local: {
          time: localTime,
          icon: isDay ? 'sunny' : 'dark_mode',
        }
      };
    });
  });

  // Provide dynamic timeZone abbreviation (e.g. PST/PDT).
  // We loop over a set of locales to naturally resolve international abbreviations (like CEST, CLT, AWST).
  formatTimeZone(timeZone: string, time: Date, locale: string): string | undefined {
    const locFormatter = new Intl.DateTimeFormat(locale, { timeZone: timeZone, timeZoneName: 'short' });
    const tzName = locFormatter.formatToParts(time).find(p => p.type === 'timeZoneName')?.value;
    if (tzName && !tzName.startsWith('GMT') && !tzName.startsWith('UTC')) {
      return tzName;
    }
    return undefined;
  }

  constructor() {
    // Update every minute.
    this.timerId = window.setInterval(() => {
      this.currentUtcSignal.set(new Date());
    }, 60_000);
  }

  ngOnDestroy(): void {
    window.clearInterval(this.timerId);
  }
}
